<template>
    <drawer-layout>
        <div class="report-container mx-7 mt-4">
            <control-panel
                :breadcrumbs="breadcrumbsItems"
                :title="$t('reports.reports')"
            />

            <template>
                <v-stepper
                    v-model="e2"
                    vertical
                    rounded
                >
                    <v-stepper-step
                        :complete="e2 > 1"
                        step="1"
                        :rules="[() => valid]"
                        error-icon="mdi-alert-circle"
                    >
                        {{ $t('reports.data_sources') }}

                        <small
                            v-if="!valid"
                            class="mt-1"
                        >
                            {{ $t('reports.errors.check_form') }}
                        </small>
                    </v-stepper-step>

                    <v-stepper-content step="1">
                        <template>
                            <v-form
                                ref="form"
                                v-model="valid"
                                lazy-validation
                                class=""
                            >
                                <v-col
                                    cols="12"
                                    md="6"
                                    class="pl-0 pt-0"
                                >
                                    <v-row>
                                        <v-col
                                            cols="12"
                                            md="12"
                                            class="py-0"
                                        >
                                            <v-subheader class="form-field-subheader">{{ $t('reports.title') }}:
                                            </v-subheader>
                                            <v-text-field
                                                v-model="form.title"
                                                :rules="rules.reportTitle"
                                                class="input-text input-text--text-field"
                                                outlined
                                                dense
                                                autofocus
                                                required
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>

                                    <v-row>
                                        <v-col
                                            cols="12"
                                            md="12"
                                            class="py-0"
                                        >
                                            <v-subheader class="form-field-subheader">{{ $t('reports.academic_period') }}:
                                            </v-subheader>
                                            <v-select
                                                class="input-text input-text--select body-text"
                                                v-model="form.academic_period"
                                                :items="sortAcademicPeriods"
                                                item-text="name"
                                                item-value="id"
                                                :rules="rules.academicPeriod"
                                                :placeholder="$t('reports.select')"
                                                outlined
                                                dense
                                                @change="getDataSources"
                                            ></v-select>
                                        </v-col>
                                    </v-row>

                                    <v-row>
                                        <v-col
                                            cols="12"
                                            md="12"
                                            class="py-0"
                                        >
                                            <v-subheader class="form-field-subheader">{{ $t('reports.report_subtype') }}:
                                            </v-subheader>
                                            <v-select
                                                class="input-text input-text--select body-text"
                                                v-model="form.report_subtype"
                                                :items="reportSubTypes"
                                                :item-text="reportSubtypeNameTranslation"
                                                item-value="id"
                                                :rules="rules.reportSubType"
                                                :placeholder="$t('reports.select')"
                                                outlined
                                                dense
                                                @change="getDataSources"
                                            >
                                                <template #no-data>
                                                    {{ $t('reports.no_data_available') }}
                                                </template>
                                            </v-select>
                                        </v-col>
                                    </v-row>

                                    <v-row>
                                        <v-container>
                                            <v-subheader class="form-field-subheader">{{ $t('reports.data_sources') }}:
                                            </v-subheader>
                                            <data-source-select
                                                v-for="(reportType, index) in requireReportTypes"
                                                :key="index"
                                                :dataSourceType="reportType"
                                                :configurations="reportConfiguration"
                                                :availableDataSources="dataSources"
                                                :dataSourcesByTypes="dataSourcesByTypes"
                                                :previousSelectedDataSources="form.data_sources"
                                                v-on:onSelectDataSources="setSelectedDataSources"
                                            />
                                            <v-btn
                                                class='btn-icon--primary btn-icon my-3'
                                                @click="dataSourceSelectors += 1"
                                                v-if="false"
                                            >
                                                <v-icon>mdi-plus</v-icon>
                                            </v-btn>
                                        </v-container>
                                    </v-row>

                                    <v-row>
                                        <v-col
                                            cols="12"
                                            md="12"
                                            class="py-0"
                                        >
                                            <v-btn
                                                class="v-btn--primary mr-3"
                                                @click="moveToReportCreationConfirmationStep"
                                                :disabled="!valid"
                                            >
                                                {{ $t('reports.continue') }}
                                            </v-btn>
                                            <v-btn
                                                class="v-btn--secondary"
                                                outlined
                                                @click="cancelReportCreation"
                                                link
                                            >
                                                {{ $t('reports.cancel') }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-form>

                            <cancel-report-generation-dialog
                                :showDialog="showDataSourceCancelGenerationDialog"
                                :reportType="reportType"
                                v-on:CancelDialog="showDataSourceCancelGenerationDialog = false"
                                v-on:ConfirmDialog="confirmCancelReportCreation"
                            />
                        </template>
                    </v-stepper-content>

                    <v-stepper-step
                        step="2"
                    >
                        {{ $t('reports.confirmation') }}
                    </v-stepper-step>

                    <v-stepper-content step="2">

                        <report-generation-confirmation
                            :reportTitle="form.title"
                            :academicPeriod="academicPeriodTitle"
                            :reportSubtype="reportSubtypeName"
                            :dataSources="dataSourceTitles"
                        />

                        <div class="pt-5">
                            <v-btn
                                class="v-btn--primary mr-3"
                                @click="saveReport"
                            >
                                {{ $t('reports.request') }}
                            </v-btn>
                            <v-btn
                                class="v-btn--secondary"
                                outlined
                                @click="cancelReportCreation"
                                link
                            >
                                {{ $t('app.cancel') }}
                            </v-btn>
                        </div>

                    </v-stepper-content>
                </v-stepper>
            </template>
        </div>

    </drawer-layout>
</template>

<script>
import {i18n} from "@/i18n";
import toastMixin from "@/Shared/toastMixin";
import Mixin from "../../../Shared/Reports/mixins/mixin"
import _ from "lodash";
import {ReportTableTwo} from "@/Repositories/Reports/Models/ReportTableTwo";

export default {

    props: {
        reportSubtypesRecords: {
            type: Object,
        },
    },

    mixins: [toastMixin,Mixin],

    data() {
        return {
            form: ReportTableTwo.build({report_type: this.reportType.id}),

            rules: {

                reportSubType: [
                    v => !!v || i18n.t('reports.create_form.required_fields.sub_type'),
                ],
            },

        }
    },

    methods: {
        /**
         * Go forward to step 2
         */
        moveToReportCreationConfirmationStep() {
            if(this.$refs.form.validate()) this.e2 = 2;
        },

        /**
         *
         */
        resetForm() {
            this.form = ReportTableTwo.build();
        },

        /**
         *
         * @param reportSubType
         * @returns {*}
         */
        reportSubtypeNameTranslation(reportSubType) {
            let name = _.get(reportSubType, 'name');
            return i18n.t(`reports.sub_types.${name}`)
        },
    },

    computed: {
        /**
         *
         * @returns {*}
         */
        reportSubTypes() {
            return _.get(this.reportSubtypesRecords, '_embedded.report_sub_types');
        },

        /**
         *
         * @returns {*|string}
         */
        reportSubtypeName() {
            let reportSubtypeRecord = _.filter(this.reportSubTypes,
                (subtype) => this.form.report_subtype === subtype.id
            ).first();

            return reportSubtypeRecord ? this.reportSubtypeNameTranslation(reportSubtypeRecord) : '';
        },
    }
}
</script>

<style scoped>
    .form-field-subheader {
        padding: 0;
    }
</style>
